import { useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useApiData } from 'lib/api';
import {
  getMergedCompetencies,
  getNumberOfMatchingCompetencies
} from 'lib/competencies';
import {
  DisplayFormattedDate,
  EmptyIndicator,
  MoreOptions,
  Pagination,
  ProgressBar,
  Search,
  SortBy,
  TitleWithIcon
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { DropdownSelectable } from 'components/common/dropdown';
import { Icon } from 'components/common/icons';
import {
  ProfileCompetenciesCompare,
  ProfileCompetencyProfileDetails,
  ProfileCompetencyProfilePinRemove,
  ProfileCompetencyProfilePinsAdd
} from '.';

import type {
  GetCompetencyProfilesRes,
  GetUserPinnedCompetencyProfilesRes,
  GetUsersRes
} from 'shared-types/responses';

type CompetencyProfile = Pick<
  GetCompetencyProfilesRes['competencyProfiles'][0],
  | 'competencies'
  | 'description'
  | 'descriptionLocales'
  | 'id'
  | 'title'
  | 'titleLocales'
>;

const competencyProfilesPerPage = 25;

const ProfileCompetencyProfiles = () => {
  const {
    language,
    messages,
    theme,
    pageState: { user },
    user: loggedInUser
  } = useReduxState();

  const router = useRouter();
  const currentPage = router.query.page ? Number(router.query.page) : 1;
  const search = ((router.query.search as string) || '').toLowerCase();
  const filter = (router.query.filter as string) || 'all';
  const sortBy = (router.query.sortBy as string) || 'progress';

  const [detailsProfileId, setDetailsProfileId] = useState<number | null>(null);

  const { data: competencyProfilesData, loading: competencyProfilesLoading } =
    useApiData<GetCompetencyProfilesRes>({
      url: '/admin/competency-profiles?limit=-1'
    });

  const { data: userData, loading: userLoading } = useApiData<GetUsersRes>({
    onlyIf: Boolean(user?.id),
    url: `/admin/users?id=${user?.id}`
  });

  const {
    data: pinnedCompetencyProfilesData,
    loading: pinnedCompetencyProfilesLoading,
    updateData: updatePinnedCompetencyProfiles
  } = useApiData<GetUserPinnedCompetencyProfilesRes>({
    onlyIf: Boolean(user?.id),
    url: `/admin/users/${user?.id}/pinned-competency-profiles`
  });

  const targetUserCompetencies = userData?.users?.[0]?.competencies || [];
  const mergedCompetencies = getMergedCompetencies(targetUserCompetencies);

  const getCompetencyProfileTitle = (competencyProfile: CompetencyProfile) =>
    competencyProfile.titleLocales?.[language] || competencyProfile.title || '';

  const competencyProfiles = competencyProfilesData?.competencyProfiles || [];
  const competencyProfilePins =
    pinnedCompetencyProfilesData?.competencyProfiles || [];

  // pinned competency profiles can be missing from the visibility-filtered list
  const listedCompetencyProfiles: CompetencyProfile[] = [
    ...competencyProfiles,
    ...competencyProfilePins.filter(
      (pin) =>
        !competencyProfiles.some(
          (competencyProfile) => competencyProfile.id === pin.id
        )
    )
  ];

  const competencyProfilesWithProgress = listedCompetencyProfiles.map(
    (competencyProfile) => {
      const numberOfMatched = getNumberOfMatchingCompetencies({
        competencies: competencyProfile.competencies,
        userCompetencies: mergedCompetencies
      });
      const numberOfTotal = competencyProfile.competencies?.length || 0;

      return {
        competencyProfile,
        numberOfMatched,
        numberOfTotal,
        pin: competencyProfilePins.find(
          (pin) => pin.id === competencyProfile.id
        ),
        progress: numberOfTotal ? numberOfMatched / numberOfTotal : 0
      };
    }
  );

  const pinnableCompetencyProfiles = competencyProfiles.filter(
    (competencyProfile) =>
      competencyProfile.canManage &&
      !competencyProfilePins.some((pin) => pin.id === competencyProfile.id)
  );

  const filteredCompetencyProfiles = competencyProfilesWithProgress
    .filter(({ competencyProfile, numberOfMatched, numberOfTotal, pin }) => {
      if (
        !getCompetencyProfileTitle(competencyProfile)
          .toLowerCase()
          .includes(search)
      ) {
        return false;
      }

      if (filter === 'pinned') return Boolean(pin);
      if (numberOfMatched === 0) return filter === 'all' && Boolean(pin);
      if (filter === 'completed') return numberOfMatched === numberOfTotal;
      if (filter === 'inProgress') return numberOfMatched < numberOfTotal;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'progress' && a.progress !== b.progress) {
        return b.progress - a.progress;
      }

      if (sortBy === 'pinned' && a.pin?.pinnedAt !== b.pin?.pinnedAt) {
        // pinned first, most recently pinned first
        return (b.pin?.pinnedAt || '').localeCompare(a.pin?.pinnedAt || '');
      }

      return getCompetencyProfileTitle(a.competencyProfile).localeCompare(
        getCompetencyProfileTitle(b.competencyProfile),
        language
      );
    });

  const numberOfPages =
    Math.ceil(filteredCompetencyProfiles.length / competencyProfilesPerPage) ||
    1;
  // unpinning the last row of the last page leaves the URL past the end
  const shownPage = Math.min(currentPage, numberOfPages);
  const competencyProfilesOnPage = filteredCompetencyProfiles.slice(
    (shownPage - 1) * competencyProfilesPerPage,
    shownPage * competencyProfilesPerPage
  );

  const detailsCompetencyProfile = listedCompetencyProfiles.find(
    (competencyProfile) => competencyProfile.id === detailsProfileId
  );
  const detailsPin = competencyProfilePins.find(
    (pin) => pin.id === detailsProfileId
  );
  const canOpenDetailsCompetencyProfile =
    competencyProfiles.some(
      (competencyProfile) => competencyProfile.id === detailsProfileId
    ) || Boolean(detailsPin?.canManage || detailsPin?.isVisible);

  const handleFilterChange = (value: string) => {
    const newQuery: Record<string, string> = { ...router.query, page: '1' };
    if (value !== 'all') newQuery.filter = value;
    else delete newQuery.filter;

    router.replace({ query: newQuery }, undefined, { shallow: true });
  };

  const handlePageChange = ({ page }: { page: number }) => {
    router.replace({ query: { ...router.query, page } }, undefined, {
      shallow: true
    });
  };

  return (
    <>
      <Box
        headerContent={
          competencyProfiles.length ? (
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10
              }}
            >
              <ProfileCompetenciesCompare
                buttonIsDropdown={false}
                competencyProfiles={competencyProfiles}
                targetUser={user}
                targetUserCompetencies={targetUserCompetencies}
              />

              {pinnedCompetencyProfilesData?.canManageUserPins &&
              pinnableCompetencyProfiles.length ? (
                <ProfileCompetencyProfilePinsAdd
                  competencyProfiles={pinnableCompetencyProfiles}
                  onPinned={updatePinnedCompetencyProfiles}
                />
              ) : null}
            </div>
          ) : null
        }
        headerContentWrap={true}
        loading={
          competencyProfilesLoading ||
          pinnedCompetencyProfilesLoading ||
          userLoading
        }
        padding={0}
        subHeaderContent={
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10,
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <div style={{ minWidth: 200 }}>
              <Search
                placeholder={messages.commonSearch}
                size="header"
                withUrl={true}
              />
            </div>

            <div style={{ alignItems: 'center', display: 'flex', gap: 10 }}>
              <DropdownSelectable
                category="lighter"
                defaultValue="all"
                onChange={handleFilterChange}
                options={[
                  { text: messages.commonAll, value: 'all' },
                  { text: messages.commonInProgress, value: 'inProgress' },
                  { text: messages.commonCompleted, value: 'completed' },
                  { text: messages.competencyProfilesPinned, value: 'pinned' }
                ]}
                selectedValueBefore={`${messages.commonShow}:`}
                showSelectedText={true}
                size="header"
                value={filter}
              />

              <SortBy
                options={[
                  { text: messages.commonName, value: 'name' },
                  { text: messages.commonProgress, value: 'progress' },
                  { text: messages.competencyProfilesPinned, value: 'pinned' }
                ]}
                value={sortBy}
                withUrl={true}
              />
            </div>
          </div>
        }
        title={messages.competencyProfiles}
        titleCount={filteredCompetencyProfiles.length}
      >
        {competencyProfilesOnPage.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell>{messages.competencyProfile}</BoxTableCell>
                <BoxTableCell>{messages.commonProgress}</BoxTableCell>
                <BoxTableCell>
                  {messages.competencyProfilePinnedUsersPinnedOn}
                </BoxTableCell>
                <BoxTableCell shrink={true} />
              </BoxTableHeader>

              {competencyProfilesOnPage.map(
                ({
                  competencyProfile,
                  numberOfMatched,
                  numberOfTotal,
                  pin,
                  progress
                }) => (
                  <BoxTableRow key={competencyProfile.id}>
                    <BoxTableCell>
                      <TitleWithIcon
                        avatarProps={{
                          fontSize: '1.1rem',
                          padding: '6px 0',
                          size: '24px'
                        }}
                        icon="address-card"
                        onClick={() =>
                          setDetailsProfileId(competencyProfile.id)
                        }
                        title={getCompetencyProfileTitle(competencyProfile)}
                        titleStyle={{ fontSize: '1.5rem', marginTop: 4 }}
                      />
                    </BoxTableCell>

                    <BoxTableCell>
                      <ProgressBar
                        color={theme.successColor}
                        label={getMessageWithPlaceholders({
                          message: messages.competenciesNumberOfCompetencies,
                          placeholders: {
                            numberOfCompetencies: `${numberOfMatched} / ${numberOfTotal}`
                          }
                        })}
                        labelStyle={{ marginBottom: 3 }}
                        progress={progress}
                        progressStyle={{ width: 200 }}
                      />
                    </BoxTableCell>

                    <BoxTableCell>
                      {pin ? (
                        <div
                          style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 8
                          }}
                        >
                          <Icon
                            icon="thumbtack"
                            style={{
                              color: theme.successStandaloneColor,
                              fontSize: '1.4rem'
                            }}
                          />
                          <DisplayFormattedDate date={pin.pinnedAt} />
                        </div>
                      ) : null}
                    </BoxTableCell>

                    <BoxTableCell shrink={true}>
                      {pin?.canManagePin ? (
                        <MoreOptions renderAsFixed={true}>
                          <ProfileCompetencyProfilePinRemove
                            competencyProfileId={competencyProfile.id}
                            onUnpinned={updatePinnedCompetencyProfiles}
                            userId={user.id}
                          />
                        </MoreOptions>
                      ) : null}
                    </BoxTableCell>
                  </BoxTableRow>
                )
              )}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator
            icon="address-card"
            title={messages.competencyProfilesEmpty}
          />
        )}
      </Box>

      <Pagination
        currentPage={shownPage}
        numberOfPages={numberOfPages}
        onPageChange={handlePageChange}
      />

      <ProfileCompetencyProfileDetails
        competencyProfile={detailsCompetencyProfile}
        includeCompetencyProfileLink={
          Boolean(loggedInUser?.roles.includes('admin')) &&
          canOpenDetailsCompetencyProfile
        }
        onHide={() => setDetailsProfileId(null)}
        userCompetencies={mergedCompetencies}
      />
    </>
  );
};

export default ProfileCompetencyProfiles;
